/* eslint-disable camelcase */
/* eslint-disable no-underscore-dangle */
/* eslint-disable jsx-a11y/label-has-associated-control */
import { useFlutterwave, closePaymentModal } from 'flutterwave-react-v3';
import { batch, useDispatch, useSelector } from 'react-redux';
import React, { useState, useContext } from 'react';
import getCurrencySymbol from 'currency-symbol-map';
import PropTypes from 'prop-types';

import { FormPaymentFieldIndicator, PayButton, UiIndicator } from '../../common-field-styles';
import PaymentModalButton from '../../common-field-components/payment-modal-button';
import { confirmPayment, setUserInputOnCtrl } from '../../../form/reducer/form';
import PaymentForm from '../../../form/components/partials/payment-form';
import { onPaymentValueChange } from '../../../form/slice-actions/form';
import { FormContext } from '../../../shared/contexts/form-context';
import { getLabel } from '../../../../shared/form-lib';
import { ControlPropType } from '../../ctrl_proptype';
import { fplsFetch } from '../../../../shared/utils';
import { logToServer } from '../../../form/utils';

export default function FlutterwaveSubComponent({ ctrl, formDesign, isRenderedOnModal }) {
  const dispatch = useDispatch();
  const { inForm, currentPageNo } = useContext(FormContext);
  const {
    form_id,
    payments,
    userAmount,
    isPayOnSubmission,
    editableForm: { flutterwave_public_key, user_custom_texts },
  } = useSelector((state) => state.form);
  const { isPaid, isError, message } = payments[ctrl.type][ctrl.internal_id] || {};
  const submitButtonText =
    inForm && (formDesign.old.submission_button_text || getLabel('submit_text', user_custom_texts) || 'Submit');
  const [openModal, setOpenModal] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState({
    email: '',
    phone: '',
    amount: ctrl.props.amount || '',
  });
  const { email, amount, phone } = paymentDetails;

  const finalAmount = Number(userAmount[ctrl.internal_id] ?? (ctrl.props.user_specify ? amount : ctrl.props.amount));

  const config = {
    public_key: flutterwave_public_key,
    tx_ref: `formplus-${Math.random()}`,
    amount: finalAmount,
    currency: ctrl.props.currency,
    payment_options: 'card,mobilemoney,ussd',
    customer: {
      email,
      phone_number: phone,
    },
    customizations: {
      custom_logo: ctrl.props.payment_logo,
      custom_title: ctrl.props.popup_title,
      custom_description: ctrl.props.popup_description,
      pay_button_text: ctrl.props.pay_button_text,
    },
  };

  const handleFlutterPayment = useFlutterwave(config);

  const handleStorePaymentValue = (status, ref, displayMsg) => {
    const newValue = `Amount Paid: ${finalAmount} ${ctrl.props.currency}\nTransaction ID: ${ref}\n`;
    batch(() => {
      if (status === 'paid') {
        dispatch(
          setUserInputOnCtrl({
            newValue,
            property: 'value',
            page: currentPageNo - 1,
            ctrlId: ctrl.internal_id,
            isValid: true,
          })
        );

        if (ctrl.value !== newValue) {
          dispatch(onPaymentValueChange());
        }
      }

      dispatch(
        confirmPayment({
          type: ctrl.type,
          isPaid: status === 'paid',
          isError: status === 'error',
          message: displayMsg,
          ctrlId: ctrl.internal_id,
          transactionId: ref,
        })
      );
    });
  };

  const onSuccess = async (response) => {
    const flutterWaveInitSuccess = response.status === 'successful';
    const transactionReference = response.tx_ref; // OR response.transaction_id
    const genericErrorMsg = 'An error occured with your payment.';
    let payload = null;

    const createLogPayload = (status, reason) => {
      const initialPayload = {
        form_id,
        transaction_id: transactionReference,
        amount: finalAmount || response.amount,
        currency: ctrl.props.currency || response.currency,
        gateway: 'Flutterwave',
      };
      const fullPayload = {
        ...initialPayload,
        status,
        reason,
      };
      return fullPayload;
    };

    // verfiy no foul play
    if (flutterWaveInitSuccess) {
      const data = {
        form_id,
        field_id: ctrl.internal_id,
        transaction_reference: transactionReference,
        amountToCharge: finalAmount,
      };

      await fplsFetch({
        method: 'POST',
        url: `/api/v2/public/forms/${form_id}/flutterwave`,
        data,
      })
        .then((res) => {
          if (res.status === 'success') {
            const displayMsg = `Payment of ${finalAmount} ${ctrl.props.currency} approved. Kindly click on “${submitButtonText}” at the bottom of the form to complete transaction`;
            handleStorePaymentValue('paid', transactionReference, displayMsg);
          } else {
            handleStorePaymentValue('error', '', res.message);
          }
          closePaymentModal();
          payload = createLogPayload(res.status, res.message);
          logToServer(payload);
        })
        .catch((error) => {
          handleStorePaymentValue('error', '', error.message || genericErrorMsg);
          closePaymentModal();
          payload = createLogPayload('error', error.message || genericErrorMsg);
          logToServer(payload);
        });
    } else {
      handleStorePaymentValue('error', '', genericErrorMsg);
      closePaymentModal();
      payload = createLogPayload('error', genericErrorMsg);
      logToServer(payload);
    }
  };

  const startPayment = (e) => {
    e.preventDefault();
    if (!flutterwave_public_key) {
      return;
    }
    setOpenModal(false);
    handleFlutterPayment({
      callback: (response) => {
        onSuccess(response);
      },
      onClose: () => {},
    });
  };

  const handleChange = (name, value) => {
    setPaymentDetails((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <>
      {isRenderedOnModal ? (
        <PaymentModalButton
          errorMessage={isError ? message || 'An error occured while paying.' : ''}
          onClick={() => inForm && flutterwave_public_key && setOpenModal(true)}
          isTestMode={false}
          isNoKeyAvailable={inForm && !flutterwave_public_key}
          isCompleted={!!(isPaid && ctrl.value)}
          amount={finalAmount}
          ctrl={ctrl}
        />
      ) : (
        <>
          {isPayOnSubmission ? (
            <FormPaymentFieldIndicator>
              There is a {ctrl.props.label} payment field attached to this form. You may be required to make payment of{' '}
              {`${getCurrencySymbol(ctrl.props.currency || 'USD')}${finalAmount}`} after submission.
            </FormPaymentFieldIndicator>
          ) : (
            <>
              {inForm && !flutterwave_public_key && (
                <UiIndicator className="status red">
                  <span>
                    The form owner has not set up their payment integration. Please contact the owner for more
                    information.
                  </span>
                </UiIndicator>
              )}

              {/* Messsage shown when error is returned from calling the payment APIs */}
              {isError && (
                <UiIndicator className="status red">
                  <span>{message || 'An error occured while paying.'}</span>
                </UiIndicator>
              )}

              {!isPaid && !ctrl.value && (
                <PayButton
                  type="button"
                  id={`fpl-${ctrl.internal_id}`}
                  bgColor="#eaa848"
                  onClick={() => inForm && flutterwave_public_key && setOpenModal(true)}>
                  {ctrl.props.pay_button_text}
                </PayButton>
              )}

              {(isPaid || ctrl.value) && (
                <UiIndicator className="paid">
                  <i className="check icon" />
                  <span>{message || ctrl.value}</span>
                </UiIndicator>
              )}
            </>
          )}
        </>
      )}

      {inForm && (
        <PaymentForm
          closeModal={() => setOpenModal(false)}
          handleOpenModal={() => setOpenModal(true)}
          openModal={openModal}
          gateway="flutterwave"
          ctrl={ctrl}
          startPayment={startPayment}
          handleChange={handleChange}
          paymentDetails={paymentDetails}
          message={message}
          finalAmount={finalAmount}
        />
      )}
    </>
  );
}

FlutterwaveSubComponent.defaultProps = {
  isRenderedOnModal: false,
  formDesign: null,
};

FlutterwaveSubComponent.propTypes = {
  ctrl: PropTypes.shape(ControlPropType).isRequired,
  isRenderedOnModal: PropTypes.bool,
  formDesign: PropTypes.shape({
    old: PropTypes.object.isRequired,
    design: PropTypes.object.isRequired,
  }),
};
